import type { BranchCategory } from '@festina/configuration-general-types/configuration'
import { BranchCategories } from '@festina-life/api'
import type { ConfigurableServer, Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useAllConciseBranches } from '../../../../uiModels/branches/useAllConciseBranches'
import { getChildHierarchy } from '../../branchesTree/helpers'

const followCategories: Record<BranchCategory, BranchCategory[]> = {
  none: BranchCategories,
  main: ['main'],
  release: ['main', 'release'],
  feature: ['main', 'release', 'feature'],
}

type Props = {
  branchName: string
  baseBranchName: Optional<string>
  server: ConfigurableServer
  category: BranchCategory
}

export const useFollowBranchCandidates = ({
  baseBranchName,
  branchName,
  server,
  category,
}: Props) => {
  const conciseBranches = useAllConciseBranches()
  const branchDescendants = useMemo(() => {
    const specificationsForSever = conciseBranches[server] ?? []
    const doesBranchExist = specificationsForSever.find(
      (specification) => specification.name === branchName,
    )

    const hierarchy: string[] = []

    if (doesBranchExist) {
      getChildHierarchy({
        conciseBranches: specificationsForSever,
        hierarchy,
        forNode: branchName,
      })
    }

    return hierarchy
  }, [branchName, conciseBranches, server])

  const allowedFollowCategories = followCategories[category]

  return useMemo(
    () =>
      conciseBranches[server]?.filter(
        (specification) =>
          allowedFollowCategories.includes(
            specification.branchCategory ?? '',
          ) &&
          !branchDescendants.includes(specification.name ?? '') &&
          specification.name !== baseBranchName &&
          specification.followsBranchName !== branchName &&
          specification.baseBranchName !== branchName,
      ) ?? [],
    [
      allowedFollowCategories,
      baseBranchName,
      branchDescendants,
      branchName,
      conciseBranches,
      server,
    ],
  )
}
